import { Css, SideNavLayoutState, useSideNavLayoutContext } from "@homebound/beam";
import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { createDraftScheduleUrl } from "src/RouteUrls";
import {
  ProjectStageProjectItemDetailFragment,
  ScheduleDetailsFragment,
  TaskAssigneeFragment,
  TaskFilterOptionsFragment,
  TaskStatusesFragment,
  TaskTradePartnerFragment,
} from "src/fragments/circularFragments.generated";
import { ProjectFeature, Stage } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import {
  useProjectSchedulePageV2Query,
  useTaskStatusesQuery,
} from "src/routes/projects/schedule-v2/ProjectSchedulePageV2.generated";
import { CondensedScheduleHeaderData } from "src/routes/projects/schedule-v2/components/CondensedScheduleHeaderData";
import { ScheduleOutOfSyncToast } from "src/routes/projects/schedule-v2/components/ScheduleOutOfSyncToast";
import { ScheduleType } from "src/routes/projects/schedule-v2/table/ScheduleType";
import { useQueryStorage } from "src/routes/projects/schedule-v2/table/filterUtils";
import { StageUrlSlug, getStageFromSlug, queryResult, stageCodeToNameMapper } from "src/utils";
import { Schedule } from "./Schedule";
import { ScheduleStoreProvider } from "./contexts/ScheduleStore";
import { TaskBillModalProvider } from "./contexts/TaskBillModalContext";
import { prepareFilterOptions } from "./scheduleUtils";

export type ProjectSchedulePageRouteParams = { projectId: string; stageSlug: StageUrlSlug };

export function ProjectSchedulePageV2() {
  const { features } = useProjectContext();
  const { projectId } = useRouteParams<ProjectSchedulePageRouteParams>();

  if (features.includes(ProjectFeature.ProductConfigPlan)) {
    return <Navigate to={createDraftScheduleUrl(projectId)} replace />;
  }

  return <ProjectSchedulePageV2Content />;
}

function ProjectSchedulePageV2Content() {
  const { features } = useProjectContext();
  const { projectId, stageSlug: stageSlugCode } = useRouteParams<ProjectSchedulePageRouteParams>();
  const stage = getStageFromSlug(stageSlugCode as StageUrlSlug);
  const query = useProjectSchedulePageV2Query({ variables: { projectId, stage }, nextFetchPolicy: "cache-first" });
  const taskStatusQuery = useTaskStatusesQuery();

  return queryResult(query, {
    data: ({ schedules, project, scheduleTasks: tasks }) => {
      if (!schedules[0]) {
        return <EmptySchedulePage stage={stage} />;
      }

      const { assignees, tradePartners } = prepareFilterOptions(tasks);

      return (
        <ScheduleStoreProvider
          scheduleType={ScheduleType.Project}
          stage={stage}
          delayFeatureFlagIsOn={features.includes(ProjectFeature.DelayFlags)}
        >
          <TaskBillModalProvider>
            <ProjectSchedulePageV2Data
              schedules={schedules}
              projectAddress={project.buildAddress?.street1 ?? ""}
              projectItems={project.stage.projectItems}
              tasks={tasks}
              marketId={project.market!.id}
              filterOptions={{ taskStatuses: taskStatusQuery.data?.taskStatuses ?? [], assignees, tradePartners }}
            />
          </TaskBillModalProvider>
        </ScheduleStoreProvider>
      );
    },
  });
}

export type ProjectSchedulePageV2DataProps = {
  // TODO: Probably want to think more about these props...
  schedules: ScheduleDetailsFragment[];
  projectItems: ProjectStageProjectItemDetailFragment[];
  tasks: TaskFilterOptionsFragment[];
  filterOptions: {
    taskStatuses: TaskStatusesFragment[];
    assignees: TaskAssigneeFragment[];
    tradePartners: TaskTradePartnerFragment[];
  };
  marketId: string;
  projectAddress: string;
};

export function ProjectSchedulePageV2Data(props: ProjectSchedulePageV2DataProps) {
  const { schedules, projectItems, filterOptions, tasks, projectAddress } = props;
  const { projectId, stageSlug } = useRouteParams<ProjectSchedulePageRouteParams>();
  const schedule = schedules[0];

  const { queryStorage, setQueryStorage } = useQueryStorage({
    storageKey: "scheduleFullScreen",
    initialQueryStorage: { fullScreen: false },
  });
  const { setNavState } = useSideNavLayoutContext();

  const { fullScreen } = queryStorage;

  // Fullscreen needs more grid width, so collapse the rail (don't hide it — the toggle stays).
  useEffect(() => {
    if (!fullScreen) return;
    let previous: SideNavLayoutState = "expanded";
    setNavState((prev) => {
      previous = prev;
      return "collapse";
    });
    return () => setNavState(previous);
  }, [fullScreen, setNavState]);

  if (!schedule) {
    return <EmptySchedulePage stage={getStageFromSlug(stageSlug as StageUrlSlug)} />;
  }

  return (
    <div data-testid="schedulesPageV2">
      <ScheduleOutOfSyncToast
        scheduleParentId={schedule.parent.id}
        scheduleStatus={schedule.status}
        stage={schedule.stage}
      />
      <div>
        {!fullScreen && (
          <CondensedScheduleHeaderData
            projectAddress={projectAddress}
            tradePartners={filterOptions.tradePartners}
            projectId={projectId}
            schedule={schedule}
          />
        )}

        <Schedule
          schedule={schedule}
          tasks={tasks}
          projectItems={projectItems}
          filterOptions={filterOptions}
          fullScreen={fullScreen}
          toggleFullScreen={() => setQueryStorage({ fullScreen: !fullScreen })}
        />
      </div>
    </div>
  );
}

function EmptySchedulePage(props: { stage: Stage }) {
  const { stage } = props;
  return (
    <div css={Css.m3.df.fdc.aic.jcc.$} data-testid="emptySchedule">
      There is no {stageCodeToNameMapper[stage]} schedule for this project.
    </div>
  );
}
